// src/components/OcrUploader.js
import { useState } from "react";
import axios from "axios";

function OcrUploader({ onOcrResult }) {
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 이미지 파일을 base64 문자열로 변환하는 함수
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // "data:image/png;base64,..." 형태 전체를 반환
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });

  // 이미지 크기 줄이기 (Cloud Vision API 권장사항)
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // 원본 크기
          let width = img.width;
          let height = img.height;
          
          // 최대 크기 제한 (800px로 축소)
          const MAX_SIZE = 800;
          if (width > MAX_SIZE || height > MAX_SIZE) {
            if (width > height) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            } else {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }
          
          // 캔버스에 이미지 그리기
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // 배경을 흰색으로 채우기 (투명도 제거)
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          
          // 이미지 그리기
          ctx.drawImage(img, 0, 0, width, height);
          
          // 항상 JPEG로 변환하고 낮은 품질로 압축 (0.8)
          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(resizedBase64);
        };
      };
    });
  };

  const handleUpload = async () => {
    if (!imageFile) {
      alert("이미지를 선택하세요.");
      return;
    }
    
    // 이미지 형식 검증
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(imageFile.type)) {
      setErrorMsg("JPG 또는 PNG 이미지만 업로드 가능합니다.");
      return;
    }
    
    // 이미지 크기 제한 (4MB로 강화)
    if (imageFile.size > 4 * 1024 * 1024) {
      setErrorMsg("이미지 크기는 4MB 이하여야 합니다.");
      return;
    }
    
    setLoading(true);
    setErrorMsg("");

    try {
      // 항상 이미지 리사이징 적용 - 크기 줄이고 JPEG로 변환
      console.log("이미지 최적화 진행 중...");
      const imageData = await resizeImage(imageFile);
      
      // 프리픽스 제거: "data:image/jpeg;base64," 부분을 제거하고 순수 base64 문자열만 전송
      const imageBase64 = imageData.split(',')[1];
      
      console.log(`이미지 변환 완료, 크기: 약 ${Math.round(imageBase64.length * 0.75 / 1024)}KB, 형식: JPEG`);
      
      // 직접 Vision API 호출 시도 (테스트용)
      try {
        console.log("API 요청 시작...");
        const response = await axios({
          method: 'post',
          url: 'https://asia-northeast3-booking-6ec89.cloudfunctions.net/extractText',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            imageBase64: imageBase64
          },
          timeout: 60000 // 60초 타임아웃 설정
        });
        
        console.log("API 응답 받음:", response.data);
        
        // OCR 결과 처리
        if (response.data && response.data.text) {
          const text = response.data.text;
          
          if (!text || text.trim() === "") {
            throw new Error("OCR 처리 결과가 비어있습니다. 다른 이미지를 시도해보세요.");
          }
          
          // 줄바꿈 문자를 공백으로 대체
          const processedText = text.replace(/\n/g, ' ');
          
          // 부모 컴포넌트로 OCR 결과 전달
          onOcrResult(processedText);
        } else {
          throw new Error("OCR 처리 결과를 가져올 수 없습니다.");
        }
      } catch (apiError) {
        console.error("API 직접 호출 오류:", apiError);
        throw apiError; // 재던지기
      }
    } catch (error) {
      console.error("OCR 업로드 에러:", error);
      
      // 오류 메시지 처리
      if (error.response) {
        const errorData = error.response.data;
        console.error("서버 응답 상세:", errorData);
        
        let errorMessage = `서버 오류 (${error.response.status})`;
        
        // 서버에서 보낸 오류 메시지가 있으면 표시
        if (typeof errorData === 'object' && errorData.error) {
          errorMessage += `: ${errorData.error}`;
        } else if (typeof errorData === 'string') {
          errorMessage += `: ${errorData}`;
        }
        
        setErrorMsg(errorMessage);
      } else if (error.request) {
        // 요청은 보냈지만 응답을 받지 못한 경우
        setErrorMsg("OCR 서버 응답 시간 초과. 잠시 후 다시 시도해주세요.");
      } else {
        // 요청 설정 과정에서 오류가 발생한 경우
        setErrorMsg(`OCR 처리 중 오류: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "1rem" }}>
      <h3>책 페이지 OCR</h3>
      <p style={{ fontSize: "0.8rem", color: "#666" }}>
        텍스트가 선명하고 배경이 깨끗한 이미지를 선택해주세요.
      </p>
      <input
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setImageFile(file);
            setErrorMsg("");
            
            // 파일 정보 출력
            console.log(`선택된 이미지: ${file.name}, 타입: ${file.type}, 크기: ${(file.size/1024).toFixed(2)}KB`);
          }
        }}
      />
      <button onClick={handleUpload} disabled={loading} style={{ marginLeft: "10px" }}>
        {loading ? "처리 중..." : "OCR 실행"}
      </button>
      {errorMsg && (
        <div style={{ color: "red", marginTop: "10px" }}>
          {errorMsg}
        </div>
      )}
      <div style={{ fontSize: "0.8rem", marginTop: "5px", color: "#666" }}>
        * 원활한 인식을 위해 이미지는 자동으로 최적화됩니다.
      </div>
    </div>
  );
}

export default OcrUploader;
